import axios, { AxiosError } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Create axios instance
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && error.config?.headers?.Authorization === 'Bearer ' + localStorage.getItem('token')) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API functions
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  signup: async (payload: {
    companyName: string;
    industry?: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    plan?: string;
    planAddons?: number;
  }) => {
    const response = await api.post('/auth/signup', payload);
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (payload: { firstName?: string; lastName?: string; phoneNumber?: string }) => {
    const response = await api.patch('/auth/profile', payload);
    return response.data;
  },

  // ADMIN: create a user inside own company (seat-quota gated server-side)
  createUser: async (payload: {
    firstName: string; lastName: string;
    email?: string; // optional — omitted → placeholder test email generated
    password?: string;
    role: 'ADMIN' | 'SAFETY_MANAGER' | 'PROJECT_MANAGER' | 'CONSTRUCTION_MANAGER' | 'CONSTRUCTION_SUPERVISOR' | 'FOREMAN' | 'CREW_MEMBER' | 'WORKER' | 'READ_ONLY'; // 'WORKER' = deprecated alias, server normalizes to CREW_MEMBER
    employeeId?: string; phoneNumber?: string;
  }) => {
    const response = await api.post('/auth/users', payload);
    return response.data;
  },

  // ADMIN: list own company's users
  listCompanyUsers: async () => {
    const response = await api.get('/auth/users');
    return response.data;
  },

  // UAT-005: seat-capacity context for the add-person flow (used/limit per
  // role bucket from the same quota source as the server's write gate).
  getSeatUsage: async () => {
    const response = await api.get('/auth/users/seat-usage');
    return response.data;
  },

  // Fix 3 (UAT): ADMIN/SUPER_ADMIN user-management actions. The temp password
  // is generated client-side and shown once — the server never echoes it.
  adminResetPassword: async (userId: number, newPassword: string, mustChangePassword = true) => {
    const response = await api.patch(`/auth/users/${userId}/password`, { newPassword, mustChangePassword });
    return response.data;
  },

  deactivateUser: async (userId: number) => {
    const response = await api.patch(`/auth/users/${userId}/deactivate`, {});
    return response.data;
  },

  reactivateUser: async (userId: number) => {
    const response = await api.patch(`/auth/users/${userId}/reactivate`, {});
    return response.data;
  },

  deleteUser: async (userId: number) => {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  },
};

export const assetApi = {
  getAll: async (filters?: { type?: string; status?: string; expiringWithinDays?: number }) => {
    const response = await api.get('/assets', { params: filters });
    return response.data;
  },

  // §8.8 batch usability gate: per-asset §4.5 verdicts for the lift picker.
  getUsability: async (ids: number[]): Promise<Record<string, { usable: boolean; reason: string | null }>> => {
    const response = await api.post('/assets/usability', { ids });
    return response.data.data as Record<string, { usable: boolean; reason: string | null }>;
  },
  
  getById: async (id: number) => {
    const response = await api.get(`/assets/${id}`);
    return response.data;
  },
  
  assign: async (assetId: number, userId: number, notes?: string) => {
    const response = await api.post('/assets/assign', { assetId, userId, notes });
    return response.data;
  },
  
  return: async (assetId: number, userId?: number) => {
    const response = await api.post(`/assets/${assetId}/return`, { userId });
    return response.data;
  },
  
  inspect: async (assetId: number, status: string, notes?: string) => {
    const response = await api.post('/assets/inspect', { assetId, status, notes });
    return response.data;
  },
  
  reportDamage: async (assetId: number, description: string, severity: string, photoUrls?: string[]) => {
    const response = await api.post('/assets/report-damage', {
      assetId,
      description,
      severity,
      photoUrls,
    });
    return response.data;
  },
  
  getExpiring: async (daysAhead: number = 90) => {
    const response = await api.get('/assets/expiring', { params: { daysAhead } });
    return response.data;
  },
  
  getCalibrationDue: async (daysAhead: number = 30) => {
    const response = await api.get('/assets/calibration-due', { params: { daysAhead } });
    return response.data;
  },
  
  getMyAssets: async () => {
    const response = await api.get('/assets/my-assets');
    return response.data;
  },
  
  create: async (data: any) => {
    const response = await api.post('/assets', data);
    return response.data;
  },
  
  update: async (id: string, data: any) => {
    const response = await api.put(`/assets/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/assets/${id}`);
    return response.data;
  },
};

export const certificationApi = {
  generateCrewShare: async (crewMemberIds: number[], projectId?: number, expiresInHours?: number) => {
    const response = await api.post('/certifications/share', {
      userIds: crewMemberIds,
      projectId,
      expiresInHours,
    });
    return response.data;
  },
  
  getCrewShare: async (shareId: string) => {
    const response = await api.get(`/certifications/share/${shareId}`);
    return response.data;
  },
  
  getUserCerts: async (userId: number) => {
    const response = await api.get(`/certifications/user/${userId}`);
    return response.data;
  },
  
  getExpiring: async (daysAhead: number = 90) => {
    const response = await api.get('/certifications/expiring', { params: { daysAhead } });
    return response.data;
  },
  
  lookupUser: async (identifier: string) => {
    const response = await api.get(`/certifications/lookup/${identifier}`);
    return response.data;
  },
  
  getMyCerts: async () => {
    const response = await api.get('/certifications/my-certs');
    return response.data;
  },
};

export const jhaApi = {
  create: async (data: any) => {
    const response = await api.post('/jha', data);
    return response.data;
  },
  
  getById: async (id: number) => {
    const response = await api.get(`/jha/${id}`);
    return response.data;
  },
  
  getProjectJHAs: async (projectId: number) => {
    const response = await api.get(`/jha/project/${projectId}`);
    return response.data;
  },
  
  getCrewPPE: async (crewMemberIds: number[], projectId: number) => {
    const response = await api.get('/jha/crew-ppe', {
      params: {
        crewMemberIds: crewMemberIds.join(','),
        projectId,
      },
    });
    return response.data;
  },
  
  getEmergencyContacts: async (projectId: number) => {
    const response = await api.get(`/jha/emergency-contacts/${projectId}`);
    return response.data;
  },
};

// ============================================================================
// Coming-Soon demand gauge (WD-T4 §10, WORKER-DASH-DECISIONS-2026-09-25 v1.2)
// Real, idempotent demand registrations — counts are always real DB rows.
// ============================================================================
export type FeatureInterestModule = 'LMS' | 'CLAIMS_OSHA' | 'RIGGING_CALCULATOR';
export const featureApi = {
  request: async (module: FeatureInterestModule) => {
    const response = await api.post('/feature-interest', { module });
    return response.data as { module: FeatureInterestModule; alreadyRequested: boolean; requestedAt: string };
  },
  getCounts: async () => {
    const response = await api.get('/feature-interest/counts');
    return response.data as { counts: Record<FeatureInterestModule, number>; requested: FeatureInterestModule[] };
  },
};

// ============================================================================
// DOT Vehicle Inspection (eDVIR) — PRD v2.7
// ============================================================================
export const dotApi = {
  // Vehicles
  listVehicles: async (status?: string) => {
    const response = await api.get('/dot/vehicles', { params: { status } });
    return response.data;
  },
  getVehicle: async (id: number) => {
    const response = await api.get(`/dot/vehicles/${id}`);
    return response.data;
  },
  createVehicle: async (data: any) => {
    const response = await api.post('/dot/vehicles', data);
    return response.data;
  },
  updateVehicle: async (id: number, data: any) => {
    const response = await api.patch(`/dot/vehicles/${id}`, data);
    return response.data;
  },
  listVehicleDvirs: async (id: number) => {
    const response = await api.get(`/dot/vehicles/${id}/dvirs`);
    return response.data;
  },
  getPriorDvir: async (vehicleId: number) => {
    const response = await api.get(`/dot/vehicles/${vehicleId}/prior-dvir`);
    return response.data;
  },
  // Assignments
  listAssignments: async (driverId?: number) => {
    const response = await api.get('/dot/assignments', { params: { driverId } });
    return response.data;
  },
  createAssignment: async (data: { vehicleId: number; driverId: number; shiftDate: string }) => {
    const response = await api.post('/dot/assignments', data);
    return response.data;
  },
  revokeAssignment: async (id: number) => {
    const response = await api.delete(`/dot/assignments/${id}`);
    return response.data;
  },
  // Drivers
  listDrivers: async () => {
    const response = await api.get('/dot/drivers');
    return response.data;
  },
  upsertDriver: async (data: any) => {
    const response = await api.post('/dot/drivers', data);
    return response.data;
  },
  // DVIRs
  listDvirs: async (params?: Record<string, any>) => {
    const response = await api.get('/dot/dvirs', { params });
    return response.data;
  },
  getDvir: async (id: number) => {
    const response = await api.get(`/dot/dvirs/${id}`);
    return response.data;
  },
  createDraft: async (body: { assignmentId: number; type: 'PRE_TRIP' | 'POST_TRIP'; odometerMi?: number; timezone?: string }) => {
    const response = await api.post('/dot/dvirs/draft', body);
    return response.data;
  },
  upsertResponses: async (dvirId: number, responses: any[]) => {
    const response = await api.put(`/dot/dvirs/${dvirId}/responses`, { responses });
    return response.data;
  },
  addDefect: async (dvirId: number, body: any) => {
    const response = await api.post(`/dot/dvirs/${dvirId}/defects`, body);
    return response.data;
  },
  // B9: supervisor/admin authorizes a NOT_APPLICABLE response
  // B14: naReason is sent end-to-end so the driver's reason reaches the authorizer
  authorizeNotApplicable: async (dvirId: number, body: { inspectionItemId: number; tirePosition?: string; naReason?: string }) => {
    const response = await api.post(`/dot/dvirs/${dvirId}/authorize-na`, body);
    return response.data;
  },
  // B12/B14: real multipart photo upload — returns a real object key
  uploadPhoto: async (dvirId: number, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await api.post(`/dot/dvirs/${dvirId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  signDvir: async (dvirId: number, body: any) => {
    const response = await api.post(`/dot/dvirs/${dvirId}/sign`, body, {
      headers: { 'Idempotency-Key': `sign-${dvirId}-${Date.now()}` },
    });
    return response.data;
  },
  createCorrection: async (dvirId: number, body: any) => {
    const response = await api.post(`/dot/dvirs/${dvirId}/correction`, body);
    return response.data;
  },
  // Acknowledgments
  signAcknowledgment: async (body: any) => {
    const response = await api.post('/dot/acknowledgments', body, {
      headers: { 'Idempotency-Key': `ack-${body.vehicleId}-${Date.now()}` },
    });
    return response.data;
  },
  // Defects / clearance / action items
  listOpenDefects: async () => {
    const response = await api.get('/dot/defects/open');
    return response.data;
  },
  getDefect: async (id: number) => {
    const response = await api.get(`/dot/defects/${id}`);
    return response.data;
  },
  recordRepairClearance: async (defectId: number, body: any) => {
    const response = await api.post(`/dot/defects/${defectId}/repair-clearance`, body, {
      headers: { 'Idempotency-Key': `clearance-${defectId}-${Date.now()}` },
    });
    return response.data;
  },
  createActionItem: async (defectId: number, body: any) => {
    const response = await api.post(`/dot/defects/${defectId}/action-item`, body);
    return response.data;
  },
  waiveDefect: async (defectId: number, reason: string) => {
    const response = await api.post(`/dot/defects/${defectId}/waive`, { reason });
    return response.data;
  },
  listActionItems: async (params?: Record<string, any>) => {
    const response = await api.get('/dot/action-items', { params });
    return response.data;
  },
  getActionItem: async (id: number) => {
    const response = await api.get(`/dot/action-items/${id}`);
    return response.data;
  },
  updateActionItem: async (id: number, body: any) => {
    const response = await api.patch(`/dot/action-items/${id}`, body);
    return response.data;
  },
  closeActionItem: async (id: number, clearanceId: number) => {
    const response = await api.post(`/dot/action-items/${id}/close`, { clearanceId });
    return response.data;
  },
  // Findings
  listFindings: async (params?: Record<string, any>) => {
    const response = await api.get('/dot/findings', { params });
    return response.data;
  },
  getFinding: async (id: number) => {
    const response = await api.get(`/dot/findings/${id}`);
    return response.data;
  },
  createFinding: async (defectId: number, body: any) => {
    const response = await api.post(`/dot/defects/${defectId}/finding`, body);
    return response.data;
  },
  updateFinding: async (id: number, body: any) => {
    const response = await api.patch(`/dot/findings/${id}`, body);
    return response.data;
  },
  createActionItemForFinding: async (findingId: number, body: any) => {
    const response = await api.post(`/dot/findings/${findingId}/action-item`, body);
    return response.data;
  },
  // Comments
  addComment: async (itemId: number, body: string, dvirId?: number, checklistVersionId?: number) => {
    const response = await api.post(`/dot/items/${itemId}/comments`, { body, dvirId, checklistVersionId });
    return response.data;
  },
  listComments: async (itemId: number) => {
    const response = await api.get(`/dot/items/${itemId}/comments`);
    return response.data;
  },
  // Attachments — B1: tirePosition support
  attachEvidence: async (dvirId: number, questionId: number, objectKey: string, tirePosition?: string) => {
    const response = await api.post(`/dot/questions/${questionId}/dvir/${dvirId}/attachments`, { objectKey, tirePosition });
    return response.data;
  },
  // Dispatch hold
  setDispatchHold: async (vehicleId: number, hold: string, reason?: string) => {
    const response = await api.post(`/dot/vehicles/${vehicleId}/dispatch-hold`, { hold, reason });
    return response.data;
  },
  // Exports — B10
  exportRecords: async (format: 'CSV' | 'PDF', dateFrom?: string, dateTo?: string) => {
    const response = await api.post('/dot/exports', { format, dateFrom, dateTo });
    return response.data;
  },
  // Admin: checklist
  listInspectionItems: async (vehicleType?: string) => {
    const response = await api.get('/dot/admin/inspection-items', { params: { vehicleType } });
    return response.data;
  },
  createInspectionItem: async (data: any) => {
    const response = await api.post('/dot/admin/inspection-items', data);
    return response.data;
  },
  updateInspectionItem: async (id: number, data: any) => {
    const response = await api.patch(`/dot/admin/inspection-items/${id}`, data);
    return response.data;
  },
  publishChecklistVersion: async (vehicleType: string) => {
    const response = await api.post('/dot/admin/checklist-versions/publish', { vehicleType });
    return response.data;
  },
  // Admin: reference items
  listReferenceItems: async () => {
    const response = await api.get('/dot/admin/reference-items');
    return response.data;
  },
  createReferenceItem: async (data: any) => {
    const response = await api.post('/dot/admin/reference-items', data);
    return response.data;
  },
  updateReferenceItem: async (id: number, data: any) => {
    const response = await api.patch(`/dot/admin/reference-items/${id}`, data);
    return response.data;
  },
  // Admin: vehicle configs
  listVehicleConfigs: async () => {
    const response = await api.get('/dot/admin/vehicle-configs');
    return response.data;
  },
  createVehicleConfig: async (data: any) => {
    const response = await api.post('/dot/admin/vehicle-configs', data);
    return response.data;
  },
  updateVehicleConfig: async (id: number, data: any) => {
    const response = await api.patch(`/dot/admin/vehicle-configs/${id}`, data);
    return response.data;
  },
  // Admin: attached units
  listAttachedUnits: async () => {
    const response = await api.get('/dot/admin/attached-units');
    return response.data;
  },
  createAttachedUnit: async (data: any) => {
    const response = await api.post('/dot/admin/attached-units', data);
    return response.data;
  },
  // Repair orders (v0.4 §1.5.1 — uniform workflow; no mechanic role)
  listRepairOrders: async (params?: { status?: string; vehicleId?: number }) => {
    const response = await api.get('/dot/repairs', { params });
    return response.data;
  },
  getRepairOrder: async (id: number) => {
    const response = await api.get(`/dot/repairs/${id}`);
    return response.data;
  },
  createRepairOrder: async (data: { vehicleId: number; defectId?: number; title: string; description?: string; outOfService?: boolean; fixerType?: 'IN_HOUSE' | 'EXTERNAL_SHOP'; externalShopName?: string; assignedFixerUserId?: number }) => {
    const response = await api.post('/dot/repairs', data);
    return response.data;
  },
  updateRepairOrder: async (id: number, data: { status?: 'IN_PROGRESS'; repairNotes?: string; description?: string; assignedFixerUserId?: number; partsCostCents?: number }) => {
    const response = await api.patch(`/dot/repairs/${id}`, data);
    return response.data;
  },
  attachRepairEvidence: async (id: number, kind: 'receipt' | 'photo', file: File, caption?: string) => {
    const formData = new FormData();
    formData.append('evidence', file);
    formData.append('kind', kind);
    if (caption) formData.append('caption', caption);
    const response = await api.post(`/dot/repairs/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  resolveRepairOrder: async (id: number, data: { resolutionSummary: string; attestationText?: string }) => {
    const response = await api.post(`/dot/repairs/${id}/resolve`, data);
    return response.data;
  },
};

// ============================================================================
// BP3 — onboarding bulk import (team + fleet CSV)
// Preview is server-side and side-effect-free; commit is idempotent via
// idempotencyKey (ImportJob replay — a retry returns the stored result).
// ============================================================================
export const importApi = {
  teamPreview: async (csv: string) => {
    const response = await api.post('/onboarding/import/team/preview', { csv });
    return response.data;
  },
  teamCommit: async (csv: string, idempotencyKey: string) => {
    const response = await api.post('/onboarding/import/team/commit', { csv, idempotencyKey });
    return response.data;
  },
  fleetPreview: async (csv: string) => {
    const response = await api.post('/onboarding/import/fleet/preview', { csv });
    return response.data;
  },
  fleetCommit: async (csv: string, idempotencyKey: string) => {
    const response = await api.post('/onboarding/import/fleet/commit', { csv, idempotencyKey });
    return response.data;
  },
};

// In-app alert inbox (UAT-R2-002 web half). A row is the record of an alert
// RAISED for the user — NOT a delivery receipt; readAt only means "seen".
export interface InboxNotification {
  id: number;
  companyId: number;
  recipientId: number;
  type: string;
  title: string;
  body: string;
  link: string | null;
  sourceKind: string | null;
  sourceId: number | null;
  readAt: string | null;
  createdAt: string;
}

export const notificationApi = {
  getAll: async (unreadOnly?: boolean) => {
    const response = await api.get('/notifications', { params: unreadOnly ? { unreadOnly: 'true' } : undefined });
    return response.data;
  },
  markRead: async (id: number) => {
    const response = await api.patch(`/notifications/${id}/read`, {});
    return response.data;
  },
};

// Incident history (AUDIT-INCIDENT-HISTORY PRD v1 S1): lookup + filter over
// the company's incident history.
export interface IncidentHistoryFilters {
  type?: string;
  severity?: string;
  dateFrom?: string;
  dateTo?: string;
  projectId?: number;
  reporterId?: number;
  investigatorId?: number;
  q?: string;
  hasOpenActions?: boolean;
  investigationStatus?: string;
  page?: number;
  limit?: number;
}

export const incidentApi = {
  getAll: async (filters?: IncidentHistoryFilters) => {
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(filters ?? {})) {
      if (v !== undefined && v !== null && v !== '') params[k] = String(v);
    }
    const response = await api.get('/incidents', { params });
    return response.data;
  },
};

export default api;

